import React, { useEffect } from 'react';
import { Helmet } from 'react-helmet';
import SimpleLayout from '@/layouts/SimpleLayout';
import MaterialIcon from '@/components/MaterialIcon';
import SmartLink from '@/components/SmartLink';
import { useTracking } from '@mygpt/app/utils';

/**
 * サッポロファクトリー 導入事例ページ
 * GBaseSupportSLP の case-study-sapporo-factory.html を移植
 */

const STATS = [
  {
    value: '90',
    unit: '%',
    suffix: '以上',
    label: '問い合わせ自動回答率',
    desc: '有人対応の負担を大幅減',
  },
  {
    value: '81',
    unit: '%',
    suffix: '削減',
    label: 'スタッフ呼び出し件数',
    desc: '導入後、前年同期の19%まで減少',
  },
  { value: '約150', label: 'ショップ・レストラン', desc: '大型施設の案内に対応' },
  { value: '2', label: '操作モード', desc: '音声・タッチの両対応' },
];

const InlineLink: React.FC<{ href: string; children: React.ReactNode }> = ({ href, children }) => (
  <a
    href={href}
    target="_blank"
    rel="noopener noreferrer"
    className="font-semibold underline underline-offset-2 hover:no-underline"
    style={{ color: '#7C3AED', textDecorationColor: 'rgba(124, 58, 237, 0.35)' }}
  >
    {children}
  </a>
);

const SELECTION_REASONS: { title: string; desc: React.ReactNode }[] = [
  {
    title: '① 高い回答精度 — 問い合わせ自動回答率 90% 以上',
    desc: (
      <>
        Sparticle 独自の RAG（検索拡張生成）技術により、約 150
        店舗の基本情報を事前学習で安定基盤として構築。一方、商業施設で頻繁に変動するイベント・催事・期間限定キャンペーン情報はリアルタイムで自動取り込み・更新するため、運営側の情報メンテナンス工数を大幅に削減できる。商業施設に特化した質問パターンに対し、安定した高精度の応答を実現する。仕組みの詳細は
        <InlineLink href="https://cs.gbase.ai/blog/chatbot-mechanism-how-it-works/">
          チャットボットの仕組み解説
        </InlineLink>
        もあわせてご参照ください。
      </>
    ),
  },
  {
    title: '② 地図連携機能 — フロアマップ自動表示',
    desc: '回答内容に応じて、該当テナントをフロアマップ上に自動でピン表示。「〇〇店はどこ？」と聞くだけで、目的の場所まで視覚的に案内できる。',
  },
  {
    title: '③ シームレスな有人切り替え — ボタン一本で人へ',
    desc: 'AI で対応しきれない複雑な問い合わせは、AI チャットからボタン操作一回で有人対応へ切り替え可能。スタッフは複雑案件に集中できる役割分担を実現。',
  },
];

const IMPLEMENTATION_DETAILS = [
  {
    title: '店舗情報の事前学習＋イベント情報のリアルタイム自動更新',
    desc: '約 150 のテナント基本情報を AI が事前学習し、安定基盤を構築。さらに頻繁に変動するイベント・催事・期間限定キャンペーン情報はリアルタイムで自動取り込み・反映するため、運営側の情報メンテナンス工数を大幅に削減できる。',
  },
  {
    title: 'チャット＋フロアマップのピン表示',
    desc: '「3階のレストランを教えて」と聞くと、該当する店舗がフロアマップ上にピン表示される。文字説明だけでなく視覚的に行き先がわかる仕組み。',
  },
  {
    title: '4 言語（日英中韓）対応のインバウンド体制',
    desc: '日本語・英語・中国語・韓国語の 4 言語に対応。訪日外国人観光客にも母国語で案内できる体制を構築している。',
  },
  {
    title: '音声・タッチ操作の直感的なインターフェース',
    desc: 'Smart Kiosk のタッチ画面と音声入力に対応。両手がふさがった状況でも音声で、文字を確認したいときはタッチで、状況に応じて入力方法を選択可能。',
  },
];

const COMPANY_INFO = [
  { icon: 'storefront', label: '業態', value: '大型商業施設運営（不動産開発）' },
  { icon: 'location_city', label: '本社所在地', value: '東京都渋谷区' },
  { icon: 'person', label: '代表者', value: '代表取締役 富岡 良之' },
];

const CHALLENGES = [
  '有人による案内業務でスタッフのリソース消費が大きい',
  '約 150 ショップの案内で、店舗位置・営業時間の問い合わせが頻発',
  'インバウンド対応で多言語コミュニケーションの強化が必要',
];

const USED_FEATURES = [
  '店舗・施設の自然言語問い合わせ応答',
  'フロアマップ連携によるピン表示案内',
  '4 言語（日英中韓）対応のインバウンド接客',
  '音声・タッチ操作の両対応（Smart Kiosk）',
];

const SapporoFactory: React.FC = () => {
  const { trackContactClick } = useTracking();

  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  return (
    <SimpleLayout>
      <Helmet>
        <title>
          サッポロファクトリー GBase Support 導入事例｜AIコンシェルジュで問い合わせ自動回答率 90% 以上を実現
        </title>
        <meta
          name="description"
          content="サッポロファクトリー（サッポロ不動産開発）が GBase Support を導入。音声・タッチ対話対応の Smart Kiosk 型 AI コンシェルジュで、約150ショップの案内業務を自動化。自動回答率 90% 以上・4 言語（日英中韓）対応を実現。"
        />
        <meta
          name="keywords"
          content="サッポロファクトリー, サッポロ不動産開発, GBase Support, AIコンシェルジュ, 商業施設 AI, 商業施設 チャットボット, フロアマップ AI, 多言語 案内, リゾーム"
        />
        <meta property="og:image" content="https://cs.gbase.ai/sapporo-factory-og.png" />
        <meta name="twitter:image" content="https://cs.gbase.ai/sapporo-factory-og.png" />
        <link href="https://fonts.googleapis.com/icon?family=Material+Icons" rel="stylesheet" />
        <link href="https://fonts.googleapis.com/css2?family=Material+Symbols+Outlined" rel="stylesheet" />
      </Helmet>

      <div className="font-solution bg-white leading-relaxed text-solution-text">
        {/* Breadcrumb */}
        <div className="bg-gray-50 border-b border-gray-100">
          <div className="max-w-7xl mx-auto px-6 py-3">
            <nav className="flex items-center gap-1 text-sm text-gray-500" aria-label="breadcrumb">
              <SmartLink to="/" className="hover:text-[#8B5CF6] transition-colors">
                ホーム
              </SmartLink>
              <MaterialIcon name="chevron_right" className="text-gray-300 mx-0.5 !text-[15px]" />
              <SmartLink to="/showcase" className="hover:text-[#8B5CF6] transition-colors">
                導入事例
              </SmartLink>
              <MaterialIcon name="chevron_right" className="text-gray-300 mx-0.5 !text-[15px]" />
              <span className="text-gray-800 font-medium">サッポロファクトリー</span>
            </nav>
          </div>
        </div>

        {/* Hero Section */}
        <section
          className="py-16 md:py-20 overflow-hidden"
          style={{ background: 'linear-gradient(135deg, #F5F3FF 0%, #FFFFFF 60%, #FFFFFF 100%)' }}
        >
          <div className="max-w-7xl mx-auto px-6">
            <div className="grid lg:grid-cols-2 gap-14 items-center">
              <div>
                <div className="flex items-center gap-4 mb-6">
                  <div
                    className="w-12 h-12 flex items-center justify-center bg-white rounded-lg border border-gray-200 flex-none"
                    style={{ boxShadow: '0 2px 8px rgba(139,92,246,0.06)' }}
                  >
                    <img
                      src="/showcase/sapporo/sapporo-re-icon.svg"
                      alt="サッポロ不動産開発 ブランドマーク"
                      className="w-7 h-7"
                      width={28}
                      height={28}
                    />
                  </div>
                  <div>
                    <p className="text-xs text-gray-500 leading-none mb-1.5">導入企業</p>
                    <p className="text-xl font-bold text-gray-900 leading-tight">サッポロ不動産開発株式会社 様</p>
                  </div>
                </div>
                <h1 className="text-3xl lg:text-4xl font-extrabold leading-tight tracking-tight mb-5">
                  <span
                    className="bg-clip-text text-transparent"
                    style={{ backgroundImage: 'linear-gradient(135deg, #7C68FB 0%, #A78BFA 100%)' }}
                  >
                    生成AIが顧客と施設をつなぐ。
                  </span>
                  <br />
                  案内業務を、AI で再設計。
                </h1>
                <p className="text-lg text-gray-600 leading-relaxed mb-8 max-w-lg">
                  北海道札幌市の大型商業施設「サッポロファクトリー」に、商業施設特化型 AI コンシェルジュ「GBase
                  Support」を導入。音声対話 × フロアマップ連携で、約 150 ショップの案内業務を自動化し、
                  <strong className="text-gray-900">問い合わせ自動回答率 90% 以上</strong>を実現。
                </p>
                <SmartLink
                  to="/contact"
                  className="inline-flex items-center gap-2 px-7 py-3 font-bold rounded-full text-white transition-all duration-300 hover:-translate-y-0.5"
                  style={{
                    background: 'linear-gradient(135deg, #8B5CF6, #A78BFA)',
                    boxShadow: '0 4px 16px rgba(139,92,246,0.3)',
                  }}
                  onClick={() => trackContactClick('case_study_sapporo_factory_hero')}
                >
                  <MaterialIcon name="mail" className="!text-lg" />
                  お問い合わせ・ご相談
                </SmartLink>
              </div>
              <div
                className="aspect-[4/3] rounded-2xl overflow-hidden max-w-md ml-auto"
                style={{ boxShadow: '0 20px 60px rgba(139,92,246,0.18), 0 8px 24px rgba(139,92,246,0.10)' }}
              >
                <img
                  src="/showcase/sapporo/sapporo-factory-ai-information-actual.jpg"
                  alt="サッポロファクトリー館内に設置された「AI INFORMATION」Smart Kiosk — タッチパネルから日本語・英語・中国語・韓国語の 4 言語で案内"
                  className="w-full h-full object-cover"
                  width={2048}
                  height={1618}
                />
              </div>
            </div>
          </div>
        </section>

        {/* Stats Section */}
        <section className="py-20" style={{ backgroundColor: '#F5F3FF' }}>
          <div className="max-w-7xl mx-auto px-6">
            <div className="text-center mb-12">
              <p className="text-sm font-semibold mb-3" style={{ color: '#8B5CF6' }}>
                導入効果
              </p>
              <h2 className="text-3xl font-bold text-gray-900">数字で見る、導入の成果</h2>
            </div>
            <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6">
              {STATS.map((stat, i) => (
                <div
                  key={i}
                  className="bg-white rounded-2xl p-8 text-center transition-all duration-300 hover:-translate-y-1 hover:shadow-xl"
                  style={{ boxShadow: '0 4px 20px rgba(139,92,246,0.06), 0 1px 3px rgba(0,0,0,0.04)' }}
                >
                  <div
                    className="text-4xl font-extrabold mb-2 bg-clip-text text-transparent"
                    style={{ backgroundImage: 'linear-gradient(135deg, #6D28D9 0%, #8B5CF6 50%, #A78BFA 100%)' }}
                  >
                    {stat.value}
                    {stat.unit && <span style={{ fontSize: '1.5rem' }}>{stat.unit}</span>}
                    {stat.suffix && <span style={{ fontSize: '1rem', color: '#5E6278' }}>{stat.suffix}</span>}
                  </div>
                  <p className="text-base text-gray-700 font-medium mb-2">{stat.label}</p>
                  <p className="text-sm text-gray-400">{stat.desc}</p>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* Main Content */}
        <div className="max-w-7xl mx-auto px-6 py-16">
          <div className="flex flex-col lg:flex-row gap-14 items-start">
            {/* LEFT: Article body */}
            <div className="flex-1 min-w-0" style={{ fontSize: 15, lineHeight: 1.85, color: '#374151' }}>
              {/* 背景 */}
              <section id="background" className="mb-12">
                <span
                  className="block text-[11px] font-bold uppercase mb-1.5"
                  style={{ color: '#8B5CF6', letterSpacing: '0.08em' }}
                >
                  導入の背景
                </span>
                <h2 className="text-lg font-bold text-[#1A1A2E] pb-3 border-b border-gray-200 mb-5">
                  歴史的商業施設が直面する案内業務の課題
                </h2>
                <p className="mb-5">
                  サッポロファクトリーは、日本人の手による初のビール工場「開拓使麦酒醸造所」の跡地に立つ大型商業施設である。敷地内には約
                  150 のショップやレストランが立ち並び、地下から 4
                  階までの吹き抜けアトリウムをシンボルとして、ガラス越しに日差しが注ぐ気持ちのよい空間が広がる。
                </p>
                <p className="mb-5">
                  これまで同施設では、有人によるインフォメーション対応で来館者からの問い合わせに対応していた。約 150
                  のショップ・レストランを擁する大規模施設において、店舗位置の問い合わせ・営業時間の確認・イベント情報の提供といった案内業務は、来館者体験の中核を担う一方で、スタッフのリソースを大きく消費していた。
                </p>
                <p className="mb-5">
                  「案内業務を AI
                  が安定的に担い、スタッフがより付加価値の高い業務に専念できる役割分担を実現したい」——その想いを起点に、商業施設特化型
                  AI コンシェルジュの導入が検討された。
                </p>
              </section>

              {/* 選定 */}
              <section id="solution" className="mb-12">
                <span
                  className="block text-[11px] font-bold uppercase mb-1.5"
                  style={{ color: '#8B5CF6', letterSpacing: '0.08em' }}
                >
                  選定の決め手
                </span>
                <h2 className="text-lg font-bold text-[#1A1A2E] pb-3 border-b border-gray-200 mb-5">
                  商業施設に最適化された AI コンシェルジュ「GBase Support」
                </h2>
                <p className="mb-5">
                  サッポロ不動産開発様が GBase Support を選定した決め手は、3 つの条件を一つの構成で満たせる点にあった。
                </p>
                <div className="space-y-5 my-6">
                  {SELECTION_REASONS.map((item, i) => (
                    <div key={i} className="pl-4 border-l-2" style={{ borderColor: '#C4B5FD' }}>
                      <h3 className="text-sm font-semibold text-gray-900 mb-1.5">{item.title}</h3>
                      <p className="text-sm text-gray-600 leading-relaxed mb-0">{item.desc}</p>
                    </div>
                  ))}
                </div>
                <p className="mb-5">
                  また、株式会社リゾーム（岡山県岡山市）との協業による導入も特徴的だ。商業施設向けに長年ソリューションを展開してきたリゾームのノウハウと、Sparticle
                  の生成 AI 技術が組み合わさることで、現場運営に即した実装が短期間で実現した。
                </p>
              </section>

              {/* 導入 */}
              <section id="implementation" className="mb-12">
                <span
                  className="block text-[11px] font-bold uppercase mb-1.5"
                  style={{ color: '#8B5CF6', letterSpacing: '0.08em' }}
                >
                  導入の仕組み
                </span>
                <h2 className="text-lg font-bold text-[#1A1A2E] pb-3 border-b border-gray-200 mb-5">
                  館内 Smart Kiosk で、音声・タッチで即座に対話
                </h2>
                <p className="mb-5">
                  来館者は施設内に設置された Smart Kiosk から、音声・タッチで AI
                  コンシェルジュと直接対話できる。専用アプリのインストールやスマートフォン操作は不要で、子どもから外国人観光客まで、誰もが直感的に利用できるインターフェースを実現している。
                </p>
                <div className="space-y-5 my-6">
                  {IMPLEMENTATION_DETAILS.map((item, i) => (
                    <div key={i} className="pl-4 border-l-2" style={{ borderColor: '#DDD6FE' }}>
                      <h3 className="text-sm font-semibold text-gray-900 mb-1.5">{item.title}</h3>
                      <p className="text-sm text-gray-600 leading-relaxed mb-0">{item.desc}</p>
                    </div>
                  ))}
                </div>
              </section>

              {/* 効果 */}
              <section id="results" className="mb-12">
                <span
                  className="block text-[11px] font-bold uppercase mb-1.5"
                  style={{ color: '#8B5CF6', letterSpacing: '0.08em' }}
                >
                  導入後の効果
                </span>
                <h2 className="text-lg font-bold text-[#1A1A2E] pb-3 border-b border-gray-200 mb-5">
                  スタッフが付加価値の高い業務に専念できる環境
                </h2>
                <p className="mb-5">
                  GBase Support の導入により、来館者からの日常的な問い合わせは AI コンシェルジュが{' '}
                  <strong>自動回答率 90% 以上</strong>{' '}
                  でカバー。営業時間中、待ち時間なく即座に案内できるため、ピーク時間帯のインフォメーション混雑も大きく緩和されている。自動回答率をはじめとする運用指標の捉え方は
                  <InlineLink href="https://cs.gbase.ai/blog/customer-success-kpi-guide-2026/">
                    カスタマーサクセス KPI ガイド
                  </InlineLink>
                  もご参考ください。
                </p>
                <p className="mb-5">
                  定型的な案内業務を AI が担うことで、
                  <strong>スタッフの呼び出し件数は導入後、前年同期の約 19%（＝約 81% 減）まで減少</strong>
                  。インフォメーションスタッフはより複雑なケース対応、来館者との深い交流、施設運営の改善業務など、
                  <strong>付加価値の高い役割</strong> に時間を使えるようになった。「AI
                  とスタッフの役割分担」が実現したことで、来館者対応の品質も平準化されている。役割シフトの考え方は
                  <InlineLink href="https://cs.gbase.ai/blog/operator-role-customer-support-2026/">
                    オペレーターの役割変遷
                  </InlineLink>
                  もあわせてご活用ください。
                </p>
                <p className="mb-5">
                  また、訪日外国人観光客の増加に対する課題感もあった中で、4 言語（日英中韓）対応の Smart Kiosk
                  は、インバウンド対応の即戦力としても機能している。
                </p>
              </section>
            </div>

            {/* RIGHT: Sidebar */}
            <aside className="w-full lg:w-72 flex-none lg:sticky lg:top-20">
              <div
                className="border border-gray-200 rounded-xl overflow-hidden"
                style={{ boxShadow: '0 2px 12px rgba(139,92,246,0.06)' }}
              >
                <div className="px-5 py-4 border-b" style={{ backgroundColor: '#F5F3FF', borderColor: '#EDE9FE' }}>
                  <p className="text-xs font-semibold uppercase tracking-wider mb-2" style={{ color: '#8B5CF6' }}>
                    導入企業
                  </p>
                  <div className="flex items-center gap-3">
                    <div
                      className="w-9 h-9 flex items-center justify-center bg-white rounded-md border border-gray-200 flex-none"
                      style={{ boxShadow: '0 1px 4px rgba(139,92,246,0.06)' }}
                    >
                      <img
                        src="/showcase/sapporo/sapporo-re-icon.svg"
                        alt="サッポロ不動産開発 ブランドマーク"
                        className="w-5 h-5"
                        width={20}
                        height={20}
                      />
                    </div>
                    <p className="text-sm font-bold text-gray-900 leading-tight whitespace-nowrap">
                      サッポロ不動産開発株式会社 様
                    </p>
                  </div>
                  <div className="mt-3 pt-3 border-t" style={{ borderColor: '#EDE9FE' }}>
                    <p className="text-xs font-semibold uppercase tracking-wider mb-1.5" style={{ color: '#8B5CF6' }}>
                      協業パートナー
                    </p>
                    <p className="text-xs text-gray-700 leading-relaxed">
                      <a
                        href="https://www.rhizome-e.com/"
                        target="_blank"
                        rel="noopener noreferrer"
                        className="text-gray-900 font-semibold hover:underline"
                      >
                        株式会社リゾーム
                      </a>
                      <br />
                      <span className="text-gray-500">商業施設向けソリューションを長年展開</span>
                    </p>
                  </div>
                </div>

                <div className="divide-y divide-gray-100">
                  {COMPANY_INFO.map((info, i) => (
                    <div key={i} className="px-5 py-3.5 flex items-start gap-3">
                      <MaterialIcon name={info.icon} className="!text-[18px] mt-0.5" style={{ color: '#A78BFA' }} />
                      <div>
                        <p className="text-xs text-gray-400 mb-0.5">{info.label}</p>
                        <p className="text-sm font-medium text-gray-800">{info.value}</p>
                      </div>
                    </div>
                  ))}
                  <div className="px-5 py-3.5 flex items-start gap-3">
                    <MaterialIcon name="link" className="!text-[18px] mt-0.5" style={{ color: '#A78BFA' }} />
                    <div>
                      <p className="text-xs text-gray-400 mb-0.5">公式サイト</p>
                      <a
                        href="https://www.sapporo-re.jp/"
                        target="_blank"
                        rel="noopener noreferrer"
                        className="text-sm font-medium hover:underline"
                        style={{ color: '#7C3AED' }}
                      >
                        sapporo-re.jp
                      </a>
                    </div>
                  </div>
                </div>

                <div className="px-5 py-4 border-t border-gray-100 bg-gray-50">
                  <p className="text-xs font-semibold text-gray-500 uppercase tracking-wider mb-3">導入施設</p>
                  <div className="space-y-2.5">
                    <div className="flex items-start gap-2">
                      <MaterialIcon name="pin_drop" className="!text-[15px] mt-0.5 text-gray-400" />
                      <div>
                        <p className="text-xs font-medium text-gray-700">サッポロファクトリー</p>
                        <p className="text-xs text-gray-400">北海道札幌市</p>
                      </div>
                    </div>
                    <div className="flex items-center gap-2">
                      <MaterialIcon name="domain" className="!text-[15px] text-gray-400" />
                      <p className="text-xs text-gray-700">約 150 ショップ・レストラン</p>
                    </div>
                    <div className="flex items-center gap-2">
                      <MaterialIcon name="history_edu" className="!text-[15px] text-gray-400" />
                      <p className="text-xs text-gray-700">日本初のビール工場跡地</p>
                    </div>
                  </div>
                </div>

                <div className="px-5 py-4 border-t border-gray-100">
                  <p className="text-xs font-semibold text-gray-500 uppercase tracking-wider mb-3">課題</p>
                  <ul className="space-y-2">
                    {CHALLENGES.map((item, i) => (
                      <li key={i} className="flex items-start gap-2 text-xs text-gray-700 leading-relaxed">
                        <span className="flex-none" style={{ color: '#A78BFA', marginTop: 1 }}>
                          •
                        </span>
                        <span>{item}</span>
                      </li>
                    ))}
                  </ul>
                </div>

                <div className="px-5 py-4 border-t border-gray-100">
                  <p className="text-xs font-semibold text-gray-500 uppercase tracking-wider mb-3">使用機能</p>
                  <ul className="space-y-2">
                    {USED_FEATURES.map((item, i) => (
                      <li key={i} className="flex items-start gap-2 text-xs text-gray-700 leading-relaxed">
                        <span className="flex-none" style={{ color: '#A78BFA', marginTop: 1 }}>
                          •
                        </span>
                        <span>{item}</span>
                      </li>
                    ))}
                  </ul>
                </div>
              </div>
            </aside>
          </div>
        </div>

        {/* CTA Section */}
        <section
          className="py-20 px-8 text-center border-t border-gray-200"
          style={{
            background:
              'radial-gradient(ellipse 55% 60% at 50% 50%, rgba(139, 92, 246, 0.22) 0%, transparent 75%), linear-gradient(180deg, rgba(139, 92, 246, 0.08) 0%, rgba(245, 158, 11, 0.04) 100%)',
          }}
        >
          <h2 className="text-2xl md:text-3xl font-extrabold mb-3.5 text-[#1A1A2E]">
            貴社の商業施設でも、AI コンシェルジュで案内業務を再設計しませんか？
          </h2>
          <p className="text-gray-600 mb-7 max-w-3xl mx-auto leading-relaxed">
            商業施設・百貨店・複合施設など、来館者対応のスケール課題を抱える組織向けに、貴施設に最適化された AI
            コンシェルジュをご提案します。
            <br />
            実機デモのご相談も承ります。
          </p>
          <SmartLink
            to="/contact"
            className="inline-flex items-center justify-center px-7 py-3 rounded-full font-semibold text-white text-sm transition-all hover:-translate-y-0.5"
            style={{
              background: 'linear-gradient(135deg, #8B5CF6 0%, #6366F1 100%)',
              boxShadow: '0 4px 14px rgba(139, 92, 246, 0.3)',
            }}
            onClick={() => trackContactClick('case_study_sapporo_factory_cta')}
          >
            お問い合わせ
          </SmartLink>
        </section>
      </div>
    </SimpleLayout>
  );
};

export default SapporoFactory;
